import React, { useState } from "react";
import { AlignLeft, ArrowRight, Bot, Check, MapPin, User, Users } from "lucide-react";
import { VbButton, CompetitorFavicon, Input, Textarea } from "@/components/vb";
import { LocationField } from "./LocationField";
import { CompetitorEditor } from "./CompetitorEditor";
import { PickerSkeleton } from "./PickerSkeleton";
import { SuggestScopeDialog } from "./SuggestScopeDialog";
import {
  describeDescriptionLength,
  MAX_DESCRIPTION,
  MAX_MARKET_CITY,
  MAX_NAME,
  MIN_DESCRIPTION,
  type BuyerWizard,
  type SuggestTarget,
} from "./useBuyerWizard";

type FieldKey = "name" | "description" | "region" | "competitors";

const FIELDS: { key: FieldKey; icon: React.ReactNode; label: string; desc: string }[] = [
  { key: "name", icon: <User size={16} />, label: "Name", desc: "What you'll call this buyer" },
  { key: "description", icon: <AlignLeft size={16} />, label: "Description", desc: "Who they are, what they care about" },
  { key: "region", icon: <MapPin size={16} />, label: "Market region", desc: "Where they live" },
  { key: "competitors", icon: <Users size={16} />, label: "Competitors", desc: "Who AI should compare you against" },
];

export function PersonaStep({
  w,
  companyName,
  companyUrl,
}: {
  w: BuyerWizard;
  companyName: string;
  companyUrl: string;
}) {
  const [active, setActive] = useState<FieldKey>("name");
  const [scopeOpen, setScopeOpen] = useState(false);

  // These ticks must track `personaComplete` exactly. `maxLength` only bounds
  // typing, so a Marcus suggestion can still write an over-long field — and a
  // rail of green ticks beside a disabled Generate button explains nothing.
  const done: Record<FieldKey, boolean> = {
    name: w.persona.name.trim().length > 0 && w.persona.name.trim().length <= MAX_NAME,
    description:
      w.persona.description.trim().length >= MIN_DESCRIPTION &&
      w.persona.description.trim().length <= MAX_DESCRIPTION,
    region:
      w.persona.market_region.trim().length > 0 &&
      w.persona.market_city.trim().length <= MAX_MARKET_CITY,
    competitors: w.competitors.length > 0,
  };

  // "Has any content" (looser than `done`) — drives the suggest dialog defaults
  // and the decision to ask before overwriting.
  const filled: Record<SuggestTarget, boolean> = {
    name: w.persona.name.trim().length > 0,
    description: w.persona.description.trim().length > 0,
    region: w.persona.market_region.trim().length > 0,
    competitors: w.competitors.length > 0,
  };
  const anyFilled = Object.values(filled).some(Boolean);

  const descHint = describeDescriptionLength(w.persona.description.trim().length);
  const busy = w.suggesting || w.generating;

  // One-click suggest only when the form is empty; otherwise open the scope
  // dialog so a suggestion never clobbers the user's effort.
  const handleSuggest = () => {
    if (busy) return;
    if (anyFilled) setScopeOpen(true);
    else void w.suggest();
  };

  // The field-card rail step's "Next" affordance: advance to the next field for
  // users who don't realise the rail is clickable. Shown once the field is done.
  const nextField = (key: FieldKey): FieldKey | null => {
    const idx = FIELDS.findIndex((f) => f.key === key);
    return idx >= 0 && idx < FIELDS.length - 1 ? FIELDS[idx + 1].key : null;
  };
  const NextButton = ({ from }: { from: FieldKey }) => {
    const next = nextField(from);
    if (!next || !done[from]) return null;
    const label = FIELDS.find((f) => f.key === next)!.label;
    return (
      <div className="pf-next">
        <VbButton type="button" variant="secondary" size="sm" onClick={() => setActive(next)}>
          Next: {label}
          <ArrowRight size={14} aria-hidden="true" />
        </VbButton>
      </div>
    );
  };

  const company = companyName.trim();

  return (
    <>
      <div className="persona-hd">
        <div className="left">
          <span className="tag">Buyer</span>
          <h3 className="persona-hd__title">
            Describe your buyer for{" "}
            {company ? (
              <span className="persona-hd__co">
                <CompetitorFavicon name={company} url={companyUrl || ""} size={18} />
                <span className="persona-hd__co-name">{company}</span>
              </span>
            ) : (
              "your company"
            )}
          </h3>
          <div className="desc">
            Type it in yourself, or let Marcus draft the whole buyer from your company →
          </div>
        </div>
        <div className="right">
          <VbButton
            type="button"
            variant={w.personaComplete ? "secondary" : "primary"}
            onClick={handleSuggest}
            disabled={busy}
          >
            <Bot size={15} aria-hidden="true" />
            {w.suggesting ? "Suggesting…" : "Suggest with Marcus"}
          </VbButton>
        </div>
      </div>

      {w.personaError && (
        <div className="limit-flash show" role="alert" style={{ marginBottom: 12 }}>
          {w.personaError}
        </div>
      )}

      {w.suggesting ? (
        <PickerSkeleton rails={4} lines={3} />
      ) : (
        <div className="picker persona-picker">
          {/* LEFT: field rail */}
          <div className="cats">
            {FIELDS.map((f) => (
              <button
                key={f.key}
                type="button"
                className={`cat-card field-card${active === f.key ? " active" : ""}`}
                onClick={() => setActive(f.key)}
              >
                <span className="ic">{f.icon}</span>
                <span className="body">
                  <span className="nm">{f.label}</span>
                  <span className="desc">{f.desc}</span>
                </span>
                <span className={`fcheck${done[f.key] ? " done" : ""}`}>
                  <Check size={12} aria-hidden="true" />
                </span>
              </button>
            ))}
          </div>

          {/* RIGHT: active field */}
          <div className="panel">
            {active === "name" && (
              <div className="field-panel active">
                <div className="panel-hd">
                  <div className="row">
                    <h3>Buyer name</h3>
                    <span className="tag">Buyer</span>
                  </div>
                  <div className="desc">A short label so you can tell your buyers apart.</div>
                </div>
                <div className="pf-input">
                  {/* Single-line field, so a hard cap is honest: the input
                      visibly stops accepting characters. The description below
                      is a textarea and gets the softer counter treatment
                      instead — truncating pasted prose there hides the loss. */}
                  <Input
                    size="lg"
                    type="text"
                    value={w.persona.name}
                    maxLength={MAX_NAME}
                    placeholder="e.g. Sarah, hail-damage homeowner"
                    onChange={(e) => w.setPersonaField("name", e.target.value)}
                    aria-label="Buyer name"
                  />
                  <span className="help">
                    Name them after who they are and why they&apos;re looking, e.g. &ldquo;Mark, growing
                    SaaS founder&rdquo; or &ldquo;Dana, clinic office manager&rdquo;. Up to {MAX_NAME}{" "}
                    characters.
                  </span>
                </div>
                <NextButton from="name" />
              </div>
            )}

            {active === "description" && (
              <div className="field-panel active">
                <div className="panel-hd">
                  <div className="row">
                    <h3>Description</h3>
                    <span className="tag">Buyer</span>
                  </div>
                  <div className="desc">Who they are, what they&apos;re trying to solve, where they search.</div>
                </div>
                <div className="pf-input">
                  <Textarea
                    size="lg"
                    value={w.persona.description}
                    placeholder="A homeowner in Orlando whose roof was just damaged in a storm, looking for a trustworthy local roofer for an insurance-claim repair. Wants fast tarping, clear pricing, and proof of past storm work before committing."
                    onChange={(e) => w.setPersonaField("description", e.target.value)}
                    aria-label="Buyer description"
                  />
                  <span className={`help${descHint.state === "over" ? " over-limit" : ""}`}>
                    The more specific the situation, the sharper the questions.{" "}
                    {descHint.text}
                  </span>
                </div>
                <NextButton from="description" />
              </div>
            )}

            {active === "region" && (
              <div className="field-panel active">
                <div className="panel-hd">
                  <div className="row">
                    <h3>Market region</h3>
                    <span className="tag">Buyer</span>
                  </div>
                  <div className="desc">Where this buyer is based. The more specific, the sharper the local context AI uses.</div>
                </div>
                <LocationField
                  region={w.persona.market_region}
                  city={w.persona.market_city}
                  onChange={({ region, city }) => w.setLocation(region, city)}
                />
                <NextButton from="region" />
              </div>
            )}

            {active === "competitors" && (
              <div className="field-panel active">
                <div className="panel-hd">
                  <div className="row">
                    <h3>Competitors</h3>
                    <span className="tag">Per buyer · optional</span>
                  </div>
                  <div className="desc">Who AI should compare you against for this buyer.</div>
                </div>
                <CompetitorEditor
                  competitors={w.competitors}
                  onAdd={(raw) => void w.addCompetitor(raw)}
                  onRemove={(c) => void w.removeCompetitor(c)}
                  error={w.competitorError}
                  busy={w.addingCompetitor || w.generating}
                />
              </div>
            )}
          </div>
        </div>
      )}

      <SuggestScopeDialog
        open={scopeOpen}
        onOpenChange={setScopeOpen}
        filled={filled}
        onConfirm={(targets) => void w.suggest(targets)}
      />
    </>
  );
}

export default PersonaStep;
